import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';

const empty = {
  titulo: '',
  resumen: '',
  contenido: '',
  imagen: '',
  categoria: 'General',
  estado: true,
  fecha_publicacion: '',
};

export default function NoticiasAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/noticias', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset({
      ...empty,
      fecha_publicacion: toLocalInput(new Date().toISOString()),
    });
    setModalOpen(true);
  }

  function toLocalInput(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  async function openEdit(row) {
    setEditingId(row.id);
    reset({
      titulo: row.titulo ?? '',
      resumen: row.resumen ?? '',
      contenido: row.contenido ?? '',
      imagen: row.imagen ?? '',
      categoria: row.categoria ?? 'General',
      estado: Number(row.estado) === 1,
      fecha_publicacion: toLocalInput(row.fecha_publicacion),
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      estado: values.estado ? 1 : 0,
      fecha_publicacion: values.fecha_publicacion
        ? new Date(values.fecha_publicacion).toISOString().slice(0, 19).replace('T', ' ')
        : undefined,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/noticias/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/noticias', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar noticia?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/noticias/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleEstado(row) {
    try {
      const current = await api.get(`/v1/noticias/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/noticias/${row.id}`, {
        ...r,
        estado: Number(r.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      { key: 'titulo', label: 'Título' },
      { key: 'categoria', label: 'Categoría' },
      {
        key: 'estado',
        label: 'Estado',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Publicada' : 'Borrador'}
          </button>
        ),
      },
      {
        key: 'fecha_publicacion',
        label: 'Publicación',
        render: (row) => new Date(row.fecha_publicacion).toLocaleString('es-CO'),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Noticias</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Editor de contenidos para la sección de historias en la landing pública.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nueva noticia
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar noticia' : 'Nueva noticia'} wide>
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Título" error={errors.titulo?.message}>
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('titulo', { required: 'Requerido' })}
            />
          </FormField>
          <FormField label="Categoría">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('categoria')}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Resumen">
              <textarea
                rows={2}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('resumen')}
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Contenido (HTML permitido)" hint="Usa párrafos simples o listas; evita scripts.">
              <textarea
                rows={10}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 font-mono text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('contenido')}
              />
            </FormField>
          </div>
          <FormField label="Fecha de publicación">
            <input
              type="datetime-local"
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('fecha_publicacion')}
            />
          </FormField>
          <div>
            <FormField label="Imagen destacada" hint={uploading ? 'Subiendo…' : undefined}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
                <input
                  type="text"
                  className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                  {...register('imagen')}
                />
              </div>
            </FormField>
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300 md:col-span-2">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green" {...register('estado')} />
            Visible en web pública
          </label>
          <div className="flex flex-wrap gap-3 md:col-span-2">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Publicar'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa" wide>
        {previewRow && (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
              {previewRow.imagen ? (
                <img src={assetUrl(previewRow.imagen)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">Sin imagen</div>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-lime">{previewRow.categoria}</p>
              <h3 className="font-display text-3xl font-bold text-white">{previewRow.titulo}</h3>
              <p className="text-sm text-slate-400">{previewRow.resumen}</p>
              <div
                className="max-w-none space-y-3 text-sm leading-relaxed text-slate-300 [&_h1]:font-display [&_h2]:font-display [&_li]:ml-4 [&_p]:text-slate-300"
                dangerouslySetInnerHTML={{ __html: previewRow.contenido || '' }}
              ></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
